// P1 OD-SNAPSHOT (spec §3.1; Adeel ruling 2026-10-06) — the farmer's last saved data, one durable store for every
// screen: IndexedDB `agxl-snapshot`, one record per (farmer, key). Written ONLY from a well-formed live read; read
// when the live read is paused, failed or slow. Not build-keyed: it survives new builds, service-worker activation
// and the app's own reloads. Cleared only on logout / farmer change / a schema bump (migration = drop).
//
// The backend is swappable: IndexedDB in the app (localStorage fallback where IndexedDB is unavailable), an in-memory
// map in unit tests.

import { createStore, del, get, keys, set, type UseStore } from 'idb-keyval';
import { postClearThumbs } from './thumbs';

export const SNAPSHOT_SCHEMA = 1;
export const SNAPSHOT_DB = 'agxl-snapshot';
export const SNAPSHOT_KEYS = ['profile', 'home_stats', 'score', 'photos', 'activity', 'agent', 'notifications', 'policy'] as const;
export type SnapshotKey = (typeof SNAPSHOT_KEYS)[number];
/** Photos kept per farmer (ruling: 50). */
export const SNAPSHOT_PHOTOS_CAP = 50;
/** Soft cap for one farmer's snapshot set (~5 MB). A write that would exceed it is refused, never truncated silently. */
export const SNAPSHOT_CAP_BYTES = 5 * 1024 * 1024;

export interface SnapshotRecord<T = unknown> {
  schema: number;
  farmer_id: string;
  key: SnapshotKey;
  /** ISO — when this key was last written from a live read. */
  written_at: string;
  data: T;
}

export interface SnapshotMeta {
  schema: number;
  farmer_id: string;
  written_at: Partial<Record<SnapshotKey, string>>;
}

export interface SnapshotBackend {
  get(k: string): Promise<unknown>;
  set(k: string, v: unknown): Promise<void>;
  del(k: string): Promise<void>;
  keys(): Promise<string[]>;
}

export function memoryBackend(seed?: Map<string, unknown>): SnapshotBackend {
  const m = seed ?? new Map<string, unknown>();
  return {
    get: async (k) => m.get(k),
    set: async (k, v) => void m.set(k, v),
    del: async (k) => void m.delete(k),
    keys: async () => Array.from(m.keys()),
  };
}

function idbBackend(): SnapshotBackend {
  const store: UseStore = createStore(SNAPSHOT_DB, 'snapshots');
  return {
    get: (k) => get(k, store),
    set: (k, v) => set(k, v, store),
    del: (k) => del(k, store),
    keys: async () => (await keys(store)).map(String),
  };
}

function localStorageBackend(): SnapshotBackend {
  const P = 'agxl-snapshot:';
  return {
    get: async (k) => {
      const raw = localStorage.getItem(P + k);
      return raw ? JSON.parse(raw) : undefined;
    },
    set: async (k, v) => void localStorage.setItem(P + k, JSON.stringify(v)),
    del: async (k) => void localStorage.removeItem(P + k),
    keys: async () => Object.keys(localStorage).filter((x) => x.startsWith(P)).map((x) => x.slice(P.length)),
  };
}

let backend: SnapshotBackend | null = null;
export function getSnapshotBackend(): SnapshotBackend {
  if (backend) return backend;
  backend = typeof indexedDB !== 'undefined' ? idbBackend() : typeof localStorage !== 'undefined' ? localStorageBackend() : memoryBackend();
  return backend;
}
/** Tests: inject a backend (memory). */
export function setSnapshotBackend(b: SnapshotBackend | null): void {
  backend = b;
}

const recKey = (farmerId: string, key: string) => `${farmerId}:${key}`;
const metaKey = (farmerId: string) => `${farmerId}:meta`;

/** A live read result the snapshot may be built from: present, not an error envelope. */
export function isWellFormed(data: unknown): boolean {
  if (data === undefined || data === null) return false;
  if (typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    if ('error' in o && o.error) return false;
  }
  return true;
}

/** The record for (farmer, key), or null when absent or from another schema (then dropped). */
export async function readSnapshot<T = unknown>(farmerId: string, key: SnapshotKey): Promise<SnapshotRecord<T> | null> {
  try {
    const rec = (await getSnapshotBackend().get(recKey(farmerId, key))) as SnapshotRecord<T> | undefined;
    if (!rec || typeof rec !== 'object') return null;
    if (rec.schema !== SNAPSHOT_SCHEMA || rec.farmer_id !== farmerId) {
      await getSnapshotBackend().del(recKey(farmerId, key)).catch(() => undefined);
      return null;
    }
    return rec;
  } catch {
    return null;
  }
}

/** Write (farmer, key) from a well-formed live read. Photos are trimmed to the cap. Returns false (and leaves the previous
 *  record untouched) for a bad body or a write that would exceed the size cap. */
export async function writeSnapshot<T = unknown>(farmerId: string, key: SnapshotKey, data: T, nowMs: number = Date.now()): Promise<boolean> {
  if (!farmerId || !isWellFormed(data)) return false;
  let body: unknown = data;
  if (key === 'photos' && Array.isArray(data)) body = data.slice(0, SNAPSHOT_PHOTOS_CAP);
  const rec: SnapshotRecord = { schema: SNAPSHOT_SCHEMA, farmer_id: farmerId, key, written_at: new Date(nowMs).toISOString(), data: body };
  try {
    const size = JSON.stringify(rec).length;
    if (size > SNAPSHOT_CAP_BYTES) {
      console.warn('[snapshot] refused: over cap', key, size);
      return false;
    }
    const b = getSnapshotBackend();
    await b.set(recKey(farmerId, key), rec);
    const meta = ((await b.get(metaKey(farmerId))) as SnapshotMeta | undefined) ?? { schema: SNAPSHOT_SCHEMA, farmer_id: farmerId, written_at: {} };
    meta.schema = SNAPSHOT_SCHEMA;
    meta.written_at = { ...meta.written_at, [key]: rec.written_at };
    await b.set(metaKey(farmerId), meta);
    return true;
  } catch (e) {
    console.warn('[snapshot] write failed', key, e);
    return false;
  }
}

/** Merge fields into an object snapshot (e.g. today's points into home_stats) without touching the rest. */
export async function patchSnapshot(farmerId: string, key: SnapshotKey, patch: Record<string, unknown>): Promise<boolean> {
  const cur = await readSnapshot<Record<string, unknown>>(farmerId, key);
  const base = cur && cur.data && typeof cur.data === 'object' && !Array.isArray(cur.data) ? cur.data : {};
  return writeSnapshot(farmerId, key, { ...base, ...patch });
}

export async function snapshotMeta(farmerId: string): Promise<SnapshotMeta | null> {
  try {
    const m = (await getSnapshotBackend().get(metaKey(farmerId))) as SnapshotMeta | undefined;
    return m && m.schema === SNAPSHOT_SCHEMA ? m : null;
  } catch {
    return null;
  }
}

/** Logout / farmer change: every record of this farmer; with no farmer id, everything (schema bump). */
export async function clearSnapshots(farmerId?: string | null): Promise<number> {
  try {
    const b = getSnapshotBackend();
    const all = await b.keys();
    const mine = farmerId ? all.filter((k) => k.startsWith(`${farmerId}:`)) : all;
    for (const k of mine) await b.del(k);
    // P2 OD-IMAGES: the farmer's cached thumbnails (service worker cache agxl-thumbs-<farmer>) go with the snapshot.
    if (farmerId) postClearThumbs(farmerId);
    return mine.length;
  } catch {
    return 0;
  }
}
